import { NAMESPACE_ITEMS } from '../../../actions/inventory/constants';
import {
  Activity,
  ActivityAPI,
  ActivityStatusKey,
  ACTIVITY_STATUS,
  AdditionalActivity,
  AdditionalActivityAPI,
  BaseItem,
  Ingredient,
  IngredientTransferAPI,
  Item,
  ItemTransfer,
  ItemTransferAPI,
  Transfer,
  TransferAPI,
  TransferStatusKey,
} from '../../../types/transfer/Transfer';

const mapStatus: {
  [key: string]: TransferStatusKey;
} = {
  CREATED: 'WAITING_FOR_APPROVAL',
  APPROVED: 'WAITING_FOR_SHIPMENT',
  REJECTED: 'REJECTED',
  REVISE_NEEDED: 'REVISE_NEEDED',
  COMPLETED: 'COMPLETED',
};

const getTotalItemTransfer = (items: BaseItem[]) => {
  return items.reduce(
    (total, item) => ({
      requestQuantity: total.requestQuantity + item.requested_quantity,
      shippedQuantity: total.shippedQuantity + item.shipped_quantity,
      notShippedQuantity: total.notShippedQuantity + item.not_shipped_quantity,
      fulfilledQuantity: total.fulfilledQuantity + item.fulfilled_quantity,
      unFulfilledQuantity:
        total.unFulfilledQuantity + item.unfulfilled_quantity,
    }),
    {
      requestQuantity: 0,
      shippedQuantity: 0,
      notShippedQuantity: 0,
      fulfilledQuantity: 0,
      unFulfilledQuantity: 0,
    }
  );
};

const handleFulfillment = (additional, itemRequestList, namespace) => {
  const isItemNamespace = namespace === NAMESPACE_ITEMS;
  const activityItemList = additional.items || additional.ingredients;

  if (!activityItemList) return;

  const newActivityList = itemRequestList.map((itemRequest) => {
    const isItemExists = activityItemList.find((activityItem) =>
      isItemNamespace
        ? itemRequest.item_variant_id === activityItem.item_variant_id
        : itemRequest.ingredient_id === activityItem.ingredient_id
    );

    if (!isItemExists) {
      return {
        ...itemRequest,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        fulfilled_quantity: 0,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        unfulfilled_quantity: 0,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        shipped_quantity: 0,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        not_shipped_quantity: 0,
      };
    }

    return isItemExists;
  });

  return newActivityList;
};

export const advancedTransferResponseTransformer = (
  transfers: TransferAPI[]
): Transfer[] => {
  return transfers.map((transfer) => {
    const itemTransfer = transfer as ItemTransferAPI;
    const ingredientTransfer = transfer as IngredientTransferAPI;

    const { status } = transfer;
    const { items } = itemTransfer;
    const { ingredients } = ingredientTransfer;
    const isItem = !!items;

    const {
      requestQuantity,
      shippedQuantity,
      notShippedQuantity,
      fulfilledQuantity,
      unFulfilledQuantity,
    } = getTotalItemTransfer(items || ingredients);

    let statusKey: TransferStatusKey;

    if (status === 'PARTIAL') {
      if (requestQuantity !== shippedQuantity + notShippedQuantity) {
        statusKey = 'PARTIALLY_SHIPPED';
      } else if (shippedQuantity !== fulfilledQuantity + unFulfilledQuantity) {
        statusKey = 'PARTIALLY_FULFILLED';
      }
    } else {
      statusKey = mapStatus[status];
    }

    const shippedPercentage =
      ((shippedQuantity + notShippedQuantity) * 100) / requestQuantity;
    const fulfilledPercentage =
      ((fulfilledQuantity + unFulfilledQuantity) * 100) / shippedQuantity;

    const transformedItem: ItemTransfer<Item> = {
      ...transfer,
      items,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      status_key: statusKey!,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      from_outlet_name: '',
      // eslint-disable-next-line @typescript-eslint/naming-convention
      to_outlet_name: '',
      // eslint-disable-next-line @typescript-eslint/naming-convention
      fulfilled_percentage: fulfilledPercentage.toFixed(),
      // eslint-disable-next-line @typescript-eslint/naming-convention
      shipped_percentage: shippedPercentage.toFixed(),
    };

    const transformedIngredient: ItemTransfer<Ingredient> = {
      ...transfer,
      items: ingredients,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      status_key: statusKey!,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      from_outlet_name: '',
      // eslint-disable-next-line @typescript-eslint/naming-convention
      to_outlet_name: '',
      // eslint-disable-next-line @typescript-eslint/naming-convention
      fulfilled_percentage: fulfilledPercentage.toFixed(),
      // eslint-disable-next-line @typescript-eslint/naming-convention
      shipped_percentage: shippedPercentage.toFixed(),
    };

    return isItem ? transformedItem : transformedIngredient;
  });
};

export const parseAdditionalActivityData = (
  additional: AdditionalActivityAPI,
  items,
  namespace
): AdditionalActivity => {
  // return each activity item whether they are shipped or not
  const fulfillmentList = handleFulfillment(additional, items, namespace);
  return {
    note: additional.note,
    reason: additional.reason,
    fulfillment: fulfillmentList || [],
    // eslint-disable-next-line @typescript-eslint/naming-convention
    shipment_id: additional.shipment_id,
  };
};

export const activityTransformer = (
  activities: ActivityAPI[],
  items,
  namespace
): Activity[] => {
  const activityStatusValues = Object.values(ACTIVITY_STATUS);
  const activityStatusKeys = Object.keys(
    ACTIVITY_STATUS
  ) as ActivityStatusKey[];

  const transformedStatusKey: Activity[] = activities.map((activity) => {
    const statusIndex = activityStatusValues.findIndex((status) =>
      status.split('|').includes(activity.status)
    );

    return {
      ...activity,
      additional: parseAdditionalActivityData(
        activity.additional,
        items,
        namespace
      ),
      // eslint-disable-next-line @typescript-eslint/naming-convention
      status_key: activityStatusKeys[statusIndex] || 'BLOCKING_REJECTED',
    };
  });

  return transformedStatusKey;
};
